/**
 * Role Form Modal Component
 * 
 * Modal for creating and editing roles with permission selection.
 * Follows UI/UX guidelines for modals and forms.
 */

import { useState, useEffect } from 'react';
import { Modal, Input, Textarea, Button, Spinner } from '@/components/ui';
import { PermissionSelector } from '../PermissionSelector';
import type { Role, PermissionCategory } from '@/services/RolesService';

interface RoleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    description: string;
    permissions: string[];
  }) => Promise<void>;
  role?: Role | null; // If provided, edit mode
  permissionCategories: PermissionCategory[];
  isLoading?: boolean;
}

export function RoleFormModal({
  isOpen,
  onClose,
  onSubmit,
  role,
  permissionCategories,
  isLoading = false,
}: RoleFormModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!role;
  const isViewMode = role?.is_system || false;

  // Populate form when role changes
  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description || '');
      setSelectedPermissions(role.permissions);
    } else {
      setName('');
      setDescription('');
      setSelectedPermissions([]);
    }
    setErrors({});
  }, [role, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Role name is required';
    }

    if (selectedPermissions.length === 0) {
      newErrors.permissions = 'At least one permission is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewMode) {
      onClose();
      return;
    }

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim(),
        permissions: selectedPermissions,
      });
      // Don't call onClose here - let the parent handle it after successful mutation
    } catch (error: any) {
      setErrors({
        submit: error.message || 'Failed to save role',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isViewMode ? 'View Role' : isEditMode ? 'Edit Role' : 'Create New Role'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Role Name */}
        <div>
          <Input
            label="Role Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Event Manager"
            error={errors.name}
            disabled={isSubmitting || isLoading || isViewMode}
            required
            autoFocus={!isViewMode}
          />
          {isViewMode && (
            <p className="mt-1 text-xs text-muted-foreground">
              System roles cannot be edited
            </p>
          )}
        </div>

        {/* Role Description */}
        <div>
          <Textarea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this role can do..."
            rows={3}
            error={errors.description}
            disabled={isSubmitting || isLoading || isViewMode}
          />
          {!isViewMode && (
            <p className="mt-1 text-xs text-muted-foreground">
              Help team members understand when to assign this role
            </p>
          )}
        </div>

        {/* Permission Selector */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Permissions
          </label>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : (
            <div className="border border-border rounded-lg p-4 max-h-96 overflow-y-auto">
              <PermissionSelector
                categories={permissionCategories}
                selectedPermissions={selectedPermissions}
                onChange={setSelectedPermissions}
                disabled={isSubmitting || isViewMode}
              />
            </div>
          )}
          {errors.permissions && (
            <p className="mt-2 text-sm text-error-600">{errors.permissions}</p>
          )}
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="rounded-md bg-error-50 p-3">
            <p className="text-sm text-error-800">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          {isViewMode ? (
            <Button
              type="button"
              onClick={onClose}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isSubmitting}>
                {isEditMode ? 'Save Changes' : 'Create Role'}
              </Button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}

